import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ChangeDetectionStrategy, inject } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { PicklistConfigService } from "config/components/picklists/picklist-config.service";
import _, { cloneDeep, first, isEmpty, isEqual } from "lodash";
import { DropdownSingleComponent } from "rl-common/components/dropdown/dropdown-single/dropdown-single.component";
import { DropdownOptions } from "rl-common/components/dropdown/dropdown.models";
import { ICharacteristicTemplate } from "rl-common/models/i-characteristic-template";
import { ICharacteristicValue } from "rl-common/models/i-characteristic-value";
import { ComponentChanges } from "rl-common/models/i-component-change";
import { CharTypeId, MaxInt, SystemIndicators } from "rl-common/rl-common.consts";
import { AccountingProcessActiveIndicator } from "rl-common/services/accounting-processes/models/accounting-process-active-indicator";
import { AllocationType } from "rl-common/services/accounting-processes/models/accounting-process-allocation-type";
import { IAccountingProcessAccount } from "rl-common/services/accounting-processes/models/i-accounting-process-account";
import { IAccountingProcessAccountGroup } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group";
import { CompanyService } from "rl-common/services/company/company.service";
import { IOneConfigWorkflowAction, IOneConfigWorkflowProcess, IOneConfigWorkflowStep } from "rl-common/services/one-config/one-config.models";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { of, Subscription } from "rxjs";
import { distinctUntilChanged, finalize, map, switchMap } from "rxjs/operators";
import { AccountingProcesses } from "../accounting-processes.consts";
import { IAccountOrGroupId } from "../build-accounting-operation/models/i-account-or-group-id";
import { IBuildAccountingOperationAccountOrGroup } from "../build-accounting-operation/models/i-build-accounting-operation-account-or-group";
import { IBuildAccountingOperationEvent } from "../build-accounting-operation/models/i-build-accounting-operation-event";
import { IBuildAccountingOperationTrigger } from "../build-accounting-operation/models/i-build-accounting-operation-trigger";
import { INullAccountOrGroup } from "../build-accounting-operation/models/i-null-account-or-group";
import { BuildAccountingProcessService } from "../build-accounting-process-modal/build-accounting-process.service";

export interface IAccountingOperationEventForm {
	id: FormControl<string>;
	statusId: FormControl<number>;
	workflowActionId: FormControl<number>;
	workflowEnabled: FormControl<boolean>;
	revAccountOrGroups: FormArray<FormControl<IBuildAccountingOperationAccountOrGroup>>;
	costAccountOrGroups: FormArray<FormControl<IBuildAccountingOperationAccountOrGroup>>;
}

@Component({
	selector: "rl-add-accounting-operation-event",
	templateUrl: "./add-accounting-operation-event.component.html",
	styleUrls: ["./add-accounting-operation-event.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ReactiveFormsModule, FormsModule, DropdownSingleComponent]
})
export class AddAccountingOperationEventComponent implements OnInit, OnChanges, OnDestroy {
	private readonly _formBuilder = inject(FormBuilder);
	private readonly _oneConfig = inject(OneConfigService);
	private readonly _companyService = inject(CompanyService);
	private readonly _buildAccountingProcessService = inject(BuildAccountingProcessService);
	private readonly _picklistConfigService = inject(PicklistConfigService);


	@Input()
	anchorTemplate: ICharacteristicTemplate;

	@Input()
	event: IBuildAccountingOperationEvent;

	@Input()
	accounts: IAccountingProcessAccount[] = [];

	@Input()
	accountGroups: IAccountingProcessAccountGroup[] = [];

	@Input()
	workflowTrigger: IBuildAccountingOperationTrigger;

	@Input()
	revenueAllocationTemplateId: number;

	@Input()
	costAllocationTemplateId: number;

	@Input()
	hasCostAllocation: boolean;

	@Input()
	hasCostAllocationTemplate: boolean;

	@Input()
	operationType: number = null;

	@Output()
	onSave = new EventEmitter<IBuildAccountingOperationEvent>();

	@Output()
	onCancel = new EventEmitter();

	accountsMap: { [id: string]: IAccountingProcessAccount };
	accountGroupsMap: { [id: string]: IAccountingProcessAccountGroup };

	form: FormGroup<IAccountingOperationEventForm>;
	formKey: string;

	wfProcess: IOneConfigWorkflowProcess;
	availableStatuses: IOneConfigWorkflowStep[] = [];
	availableWfActions: IOneConfigWorkflowAction[] = [nullWfAction];
	resultingStatus: IOneConfigWorkflowStep = nullResultingStatus;

	AllocationType = AllocationType;
	revenueAllocationTemplate: ICharacteristicTemplate;
	costAllocationTemplate: ICharacteristicTemplate;

	displayFeeType: boolean = false;
	feeTypes: string[] = ["Fee Type A", "Fee Type B"];
	feeTypeItems: ICharacteristicValue[] = [];
	feeTypeOptions: DropdownOptions<ICharacteristicValue> = {
		items: this.feeTypeItems,
		rowKeyFn: (type: ICharacteristicValue) => type.charValueID,
		rowLabelFn: (type: ICharacteristicValue) => type.charValueLabel
	};
	readonly defaultFeeType = { charValueID: 0, charValueLabel: "None" } as ICharacteristicValue;

	private _formSubs: Subscription[] = [];
	private readonly _subs: Subscription[] = [];

	get availableAccounts() {
		return this.accounts?.filter(x => x.activeIndicator === AccountingProcessActiveIndicator.Active) ?? [];
	}

	get availableAccountGroups() {
		return this.accountGroups;
	}

	async ngOnInit(): Promise<void> {
		this.accountGroups = this.accountGroups.filter(g => g.ruleCount > 0);

		const amountTemplates = this._oneConfig.getTemplates(CharTypeId.Amount);
		this.revenueAllocationTemplate = amountTemplates.find(t => t.templateID === this.revenueAllocationTemplateId);
		this.costAllocationTemplate = amountTemplates.find(t => t.templateID === this.costAllocationTemplateId);

		if (this.anchorTemplate) {
			this.setStatuses();
		}
		this.setWfProcess();
		this.buildForm(this.event, true);
		this.buildAccountsMap();
		this.buildAccountGroupsMap();

		await this.setFeeTypeItems();

		this.displayFeeType = !!this.operationType && this.operationType === AccountingProcesses.OperationType.CashRelevant;
	}

	ngOnChanges(changes: ComponentChanges<this>): void {
		if (changes.anchorTemplate && changes.anchorTemplate.currentValue) {
			this.setWfProcess();
			this.setStatuses();
		}

		if (changes.event && changes.event.previousValue?.id !== changes.event.currentValue?.id) {
			this.buildForm(this.event, changes.event?.firstChange);
		}

		if (changes.accounts) {
			this.buildAccountsMap();
		}

		if (changes.accountGroups) {
			this.buildAccountGroupsMap();
		}

		if (changes.hasCostAllocation && this.form) {
			this.buildForm(this.event, false);
		}

		if (changes.hasCostAllocationTemplate && this.form) {
			const previousHadTemplate = changes.hasCostAllocationTemplate.previousValue;
			const currentHasTemplate = changes.hasCostAllocationTemplate.currentValue;

			// If template presence changed, rebuild the form to add/remove cost account controls
			if (previousHadTemplate !== currentHasTemplate) {
				this.buildForm(this.event, false);
			}
		}

		if (!isEqual(changes.workflowTrigger?.currentValue, changes.workflowTrigger?.previousValue)) {
			const workflowDisabled = this.form && this.form.controls.id.value && this.form.controls.statusId.value == null && this.form.controls.workflowActionId.value == null;
			if (!workflowDisabled) {
				this.updateStatusIdControl(this.event, changes.event?.firstChange);
			}
		}
	}

	async setFeeTypeItems() {
		const feeType = this._oneConfig.getCharBySystemIndicator(CharTypeId.Amount, SystemIndicators.FeeType);
		if (feeType) {
			this.feeTypeItems = cloneDeep(await this._picklistConfigService.getPicklistSourceValues(feeType.charValueSetID).toPromise());
			this.feeTypeItems.unshift(this.defaultFeeType);
			this.feeTypeOptions.items = this.feeTypeItems;
		}
	}

	getAnchorTemplateText() {
		if (this.revenueAllocationTemplate) {
			return this.revenueAllocationTemplate.templateName
		}
		if (this.anchorTemplate) {
			return this.anchorTemplate.templateName;
		}
		return "";
	}

	getCostAllocationTemplateText() {
		if (this.costAllocationTemplate) {
			return this.costAllocationTemplate.templateName;
		}
		return "";
	}

	getAnchorAndCostAllocationTemplateText() {
		return this.getAnchorTemplateText() + ", " + this.getCostAllocationTemplateText();
	}

	setWfProcess() {
		const templateProcessId = this._oneConfig.getTemplateProcess(CharTypeId.Amount, this.anchorTemplate?.templateID);
		this.wfProcess = this._oneConfig.getCharTypeProcesses(CharTypeId.Amount).find(x => x.processId === templateProcessId);
	}

	setStatuses() {
		const statuses = this._oneConfig.getTemplateStatuses(CharTypeId.Amount, this.anchorTemplate.templateID);
		this.availableStatuses = [nullStatus].concat(statuses);
	}

	buildAccountsMap() {
		this.accountsMap = this.accounts.reduce<{ [id: string]: IAccountingProcessAccount }>((dict, account) => {
			dict[account.id] = account;
			return dict;
		}, {});
	}

	buildAccountGroupsMap() {
		this.accountGroupsMap = this.accountGroups.reduce<{ [id: string]: IAccountingProcessAccountGroup }>((dict, accountGroup) => {
			dict[accountGroup.id] = accountGroup;
			return dict;
		}, {});
	}

	buildForm(event: IBuildAccountingOperationEvent = null, initialLoad = false) {
		this._formSubs.forEach(sub => sub.unsubscribe());
		this._formSubs = [];

		const defaultEvent = this.defaultEvent();
		const merged = { ...defaultEvent, ...event };

		for (let i = 0; i < merged.accountOrGroups.length; i++) {
			const accGroup = merged.accountOrGroups[i];
			merged.accountOrGroups[i] = { ...this.defaultAccountOrGroup(accGroup.allocationType), ...accGroup };
		}

		const revAccountOrGroups = event.id ? merged.accountOrGroups.filter(x => x.allocationType === AllocationType.Revenue) : cloneDeep(merged.accountOrGroups);
		// Only load cost accounts if we have a cost allocation template selected
		let costAccountOrGroups = event.id && this.hasCostAllocationTemplate
			? merged.accountOrGroups.filter(x => x.allocationType === AllocationType.Cost)
			: cloneDeep(merged.accountOrGroups);

		// If we have a cost allocation template but no cost accounts, initialize with a default one
		if (this.hasCostAllocationTemplate && costAccountOrGroups.length === 0) {
			costAccountOrGroups = [this.defaultAccountOrGroup(AllocationType.Cost)];
		}

		const revenueAccountOrGroupControls = revAccountOrGroups.map(x => new FormControl<IBuildAccountingOperationAccountOrGroup>(x, [accountRequiredValidator]));
		const costAccountOrGroupControls = costAccountOrGroups.map(x => new FormControl<IBuildAccountingOperationAccountOrGroup>(x, [accountRequiredValidator]));
		this.form = this._formBuilder.group({
			id: new FormControl<string>(merged.id),
			statusId: new FormControl<number>(merged.statusId, [Validators.required]),
			workflowActionId: new FormControl<number>(merged.workflowActionId, [Validators.required]),
			revAccountOrGroups: new FormArray<FormControl<IBuildAccountingOperationAccountOrGroup>>(revenueAccountOrGroupControls),
			costAccountOrGroups: this.hasCostAllocation && this.hasCostAllocationTemplate ? new FormArray<FormControl<IBuildAccountingOperationAccountOrGroup>>(costAccountOrGroupControls) : null,
			workflowEnabled: new FormControl<boolean>(true)
		}, { validators: this.hasCostAllocation && this.hasCostAllocationTemplate ? [this.revAccountsValidator, this.costAccountsValidator] : this.revAccountsValidator });

		const statusSub = this.form.controls.statusId.valueChanges.pipe(
			distinctUntilChanged(),
			switchMap(stepId => {
				if (!stepId || !this.wfProcess) {
					return of([]);
				}
				const status = this.availableStatuses.find(x => x.stepId === stepId);
				return this._companyService.getWfStepActionAssociations(this.wfProcess.processId, status.stepId).pipe(
					map(associations => {
						const availableActions = this.wfProcess.workflowActions.filter(action => associations.find(x => x.actionID === action.actionId));
						return availableActions;
					}),
					finalize(() => this.form.controls.workflowActionId.enable())
				);
			})
		).subscribe(availableActions => {
			this.availableWfActions = [nullWfAction].concat(availableActions);
			const workflowActionId = this.form.controls.workflowActionId.value;
			if (!this.availableWfActions.find(x => x.actionId === workflowActionId)) {
				this.form.controls.workflowActionId.setValue(nullWfAction.actionId);
			}
		});

		const wfActionIdSub = this.form.controls.workflowActionId.valueChanges.pipe(
			distinctUntilChanged(),
			switchMap(actionId => {
				if (actionId === nullWfAction.actionId || !this.wfProcess) {
					return of([]);
				}
				return this._companyService.getWfActionStepAssociations(this.wfProcess.processId, actionId).pipe(
					map(associations => this.wfProcess.workflowSteps.filter(step => associations.find(assoc => assoc.stepID === step.stepId)))
				);
			})
		).subscribe(steps => {
			this.resultingStatus = first(steps) ?? nullResultingStatus;
		});

		if (event.statusId) {
			this.form.controls.statusId.setValue(event.statusId, { emitEvent: true });
		}

		const wfDisabledSub = this.form.controls.workflowEnabled.valueChanges.subscribe((wfEnabled) => {
			if (wfEnabled) {
				this.form.controls.statusId.enable({ emitEvent: false });
				this.form.controls.workflowActionId.enable({ emitEvent: false });
				this.updateStatusIdControl(event, initialLoad);
			} else {
				this.form.controls.statusId.setValue(nullStatus.stepId);
				this.form.controls.statusId.disable();
				this.form.controls.workflowActionId.setValue(nullWfAction.actionId);
				this.form.controls.workflowActionId.disable();
			}
		});

		const workflowDisabled = event.id && event.statusId == null && event.workflowActionId == null;
		if (workflowDisabled) {
			this.form.controls.workflowEnabled.setValue(!workflowDisabled);
		} else {
			this.form.controls.statusId.enable({ emitEvent: false });
			this.updateStatusIdControl(event, initialLoad);
		}

		this._formSubs.push(statusSub, wfActionIdSub, wfDisabledSub);

		this.formKey = `AddAccountingOperationEvent-${event?.id ?? "0"}`;
		this._buildAccountingProcessService.registerForm(this.formKey, this.form);
	}

	compareAccountOrGroup(aog1: IAccountOrGroupId, aog2: IAccountOrGroupId) {
		return aog1?.id === aog2?.id;
	}

	private defaultEvent(event: IBuildAccountingOperationEvent = null) {
		const statusId = event?.statusId ?? nullStatus.stepId;
		const workflowActionId = event?.workflowActionId ?? nullWfAction.actionId;
		const accountOrGroups = event?.accountOrGroups ?? [this.defaultAccountOrGroup(null)];

		const defaultEvent: IBuildAccountingOperationEvent = {
			statusId,
			workflowActionId,
			accountOrGroups
		} as IBuildAccountingOperationEvent;
		return defaultEvent;
	}

	private defaultAccountOrGroup(allocType: AllocationType) {
		return {
			debitId: { id: nullAccountOrGroup.id, isGroup: false },
			creditId: { id: nullAccountOrGroup.id, isGroup: false },
			debitName: nullAccountOrGroup.name,
			creditName: nullAccountOrGroup.name,
			allocationType: allocType,
			feeTypeCharVal: this.defaultFeeType
		} as IBuildAccountingOperationAccountOrGroup;
	}

	private updateStatusIdControl(ev: IBuildAccountingOperationEvent = null, initialLoad = false) {
		if (!this.form) {
			return;
		}
		const defaultEvent = this.defaultEvent(ev);
		if (this.workflowTrigger && !initialLoad) {
			const processId = this._oneConfig.getTemplateProcess(CharTypeId.Amount, this.anchorTemplate.templateID);
			const wfProcess = this._oneConfig.getCharTypeProcesses(CharTypeId.Amount).find(x => x.processId === processId);
			const sub = this._companyService.getWfActions(1, MaxInt.Num, processId).subscribe(response => {
				const resultingWorkflowActions = response.data;
				const resultingStepName = resultingWorkflowActions.find(x => x.actionID === this.workflowTrigger.actionId)?.stepName;
				const stepId = wfProcess.workflowSteps.find(x => x.stepName === resultingStepName)?.stepId;
				if (!stepId) {
					this.form.controls.workflowEnabled.disable({ emitEvent: false });
					this.form.controls.workflowEnabled.setValue(false, { emitEvent: true });
				} else {
					this.form.controls.statusId.setValue(stepId ?? defaultEvent.statusId, { emitEvent: true });
				}
			});
			this._subs.push(sub);
		} else {
			this.form.controls.statusId.setValue(defaultEvent.statusId, { emitEvent: true });
		}
	}

	availableDebitAccounts(control: FormControl<IBuildAccountingOperationAccountOrGroup>) {
		const accountOrGroup = control.value;
		return [nullAccountOrGroup].concat(this.availableAccounts.filter(x => x.id !== accountOrGroup.creditId.id));
	}

	availableCreditAccounts(control: FormControl<IBuildAccountingOperationAccountOrGroup>) {
		const accountOrGroup = control.value;
		return [nullAccountOrGroup].concat(this.availableAccounts.filter(x => x.id !== accountOrGroup.debitId.id));
	}

	availableDebitAccountGroups(control: FormControl<IBuildAccountingOperationAccountOrGroup>) {
		const accountOrGroup = control.value;
		return this.availableAccountGroups.filter(x => x.id !== accountOrGroup.creditId.id);
	}

	availableCreditAccountGroups(control: FormControl<IBuildAccountingOperationAccountOrGroup>) {
		const accountOrGroup = control.value;
		return this.availableAccountGroups.filter(x => x.id !== accountOrGroup.debitId.id);
	}

	removeAccount(index: number, allocType: AllocationType) {
		const formArray = allocType === AllocationType.Revenue ? this.form.controls.revAccountOrGroups : this.form.controls.costAccountOrGroups;
		formArray.removeAt(index);
	}

	addAnotherAccountOrGroup(allocType: AllocationType) {
		const defaultAccountOrGroup = this.defaultAccountOrGroup(allocType);
		const formArray = allocType === AllocationType.Revenue ? this.form.controls.revAccountOrGroups : this.form.controls.costAccountOrGroups;
		formArray.push(new FormControl<IBuildAccountingOperationAccountOrGroup>(defaultAccountOrGroup, [accountRequiredValidator]));
	}

	updateAccountOrGroupControl(index: number, allocationType: AllocationType) {
		const controls = allocationType === AllocationType.Revenue ? this.form.controls.revAccountOrGroups.controls : this.form.controls.costAccountOrGroups.controls;
		const control = controls[index];
		const account = control.value;
		const debitId = account.debitId;
		const creditId = account.creditId;
		const feeType = account.feeTypeCharVal.charValueID;
		const feeTypeCharVal = account.feeTypeCharVal;

		const debitName = debitId.id in this.accountsMap ? this.accountsMap[debitId.id].name :
			debitId.id in this.accountGroupsMap ? this.accountGroupsMap[debitId.id].groupName : "";
		const creditName = creditId.id in this.accountsMap ? this.accountsMap[creditId.id].name :
			creditId.id in this.accountGroupsMap ? this.accountGroupsMap[creditId.id].groupName : "";

		const result: IBuildAccountingOperationAccountOrGroup = {
			debitId,
			creditId,
			debitName,
			creditName,
			allocationType,
			feeType,
			feeTypeCharVal
		} as IBuildAccountingOperationAccountOrGroup;
		control.setValue(result);
		this.form.markAsDirty();
	}

	showCostAccountsRequiredError(): boolean {
		// Show error if cost template is selected but no valid cost accounts exist
		if (!this.hasCostAllocationTemplate || !this.form?.controls.costAccountOrGroups) {
			return false;
		}

		const hasValidCostAccount = this.form.controls.costAccountOrGroups.controls.some(control => {
			const account = control.value;
			return account.debitId?.id != null && account.creditId?.id != null;
		});

		return !hasValidCostAccount;
	}

	showRevAccountsRequiredError(): boolean {
		// Show error if no valid revenue accounts exist (both debit and credit selected)
		if (!this.form?.controls.revAccountOrGroups) {
			return false;
		}

		const hasValidRevAccount = this.form.controls.revAccountOrGroups.controls.some(control => {
			const account = control.value;
			return account.debitId?.id != null && account.creditId?.id != null;
		});

		return !hasValidRevAccount;
	}

	cancel() {
		this.onCancel.emit();
	}

	save() {
		// Only include revenue accounts that have valid debit/credit selections (not null/"Select")
		const revAccountOrGroups = this.form.controls.revAccountOrGroups?.controls
			?.map(control => control.value)
			.filter(x => x.debitId?.id != null && x.creditId?.id != null) || [];
		// Only include cost accounts if we have both cost allocation enabled AND a template selected
		// AND the cost accounts have valid debit/credit selections (not null/"Select")
		const costAccountOrGroups = this.hasCostAllocation && this.hasCostAllocationTemplate && this.form.controls.costAccountOrGroups
			? this.form.controls.costAccountOrGroups.controls
				.map(control => control.value)
				.filter(x => x.debitId?.id != null && x.creditId?.id != null)
			: [];
		const accountOrGroups = revAccountOrGroups.concat(costAccountOrGroups);
		const event: IBuildAccountingOperationEvent = {
			id: this.form.controls.id.value,
			statusId: this.form.controls.statusId.value,
			workflowActionId: this.form.controls.workflowActionId.value,
			accountOrGroups
		};
		this.onSave.emit(event);
	}

	ngOnDestroy(): void {
		this._formSubs.forEach(sub => sub.unsubscribe());
		if (this.formKey) {
			this._buildAccountingProcessService.unregisterForm(this.formKey);
		}
		this._subs.forEach(sub => sub.unsubscribe());
	}

	revAccountsValidator: ValidatorFn = (form: FormGroup<IAccountingOperationEventForm>): ValidationErrors | null => {
		if (isEmpty(form.controls.revAccountOrGroups.controls)) {
			return { revAccountsRequired: true };
		}
		return this.accountsValidator(form, AllocationType.Revenue)
	};

	costAccountsValidator: ValidatorFn = (form: FormGroup<IAccountingOperationEventForm>): ValidationErrors | null => {
		if (isEmpty(form.controls.costAccountOrGroups.controls)) {
			return { costAccountsRequired: true };
		}
		return this.accountsValidator(form, AllocationType.Cost)
	};

	accountsValidator = (form: FormGroup<IAccountingOperationEventForm>, allocType: AllocationType): ValidationErrors | null => {
		const accountsFormArray = allocType === AllocationType.Revenue ? form.controls.revAccountOrGroups : form.controls.costAccountOrGroups;
		const accounts = accountsFormArray.controls.map(x => x.value);
		const allAccounts = accounts.map(x => x.creditId.id + (x.creditId.id !== null && allocType === AllocationType.Revenue && this.operationType === AccountingProcesses.OperationType.CashRelevant ? x.feeType : null))
			.concat(accounts.map(x => x.debitId.id + (x.creditId.id !== null && allocType === AllocationType.Revenue && this.operationType === AccountingProcesses.OperationType.CashRelevant ? x.feeType : null)))
			.filter(id => !!id);
		const duplicateAccounts = _(allAccounts)
			.groupBy(accountId => accountId)
			.filter(grp => grp.length > 1)
			.value();

		if (!isEmpty(duplicateAccounts)) {
			return allocType === AllocationType.Revenue ? { revAccountUniqueness: true } : { costAccountUniqueness: true };
		}

		return null;
	}
}

const nullResultingStatus = { stepId: null, stepName: "No Status change" } as IOneConfigWorkflowStep;
const nullStatus: IOneConfigWorkflowStep = { stepId: null, stepName: "Select" } as IOneConfigWorkflowStep;
const nullWfAction: IOneConfigWorkflowAction = { actionId: null, actionName: "Select" } as IOneConfigWorkflowAction;
const nullAccountOrGroup: INullAccountOrGroup = { id: null, name: "Select" } as INullAccountOrGroup;

export const accountRequiredValidator: ValidatorFn = (control: FormControl<IBuildAccountingOperationAccountOrGroup>): ValidationErrors | null => {
	const account = control.value;
	if (account.debitId.id === nullAccountOrGroup.id || account.creditId.id === nullAccountOrGroup.id) {
		return { accountRequired: true };
	}
	return null;
};
